// ensure to bind ipc as first thing
import "./ipc";

// then import other things
import { GitpodAPI } from "@/api";
import { App } from "@/app";
import { ExceptionErrorBoundary } from "@/components/ExceptionHandler";
import { Toaster } from "@/components/podkit/toasts/Toaster";
import { GitpodAPIContext } from "@/contexts/GitpodAPIContext";
import { OnboardingProvider } from "@/contexts/OnboardingProvider";
import { OnboardingQuestionnaireProvider } from "@/contexts/OnboardingQuestionnaireProvider";
import { createSegment } from "@/contexts/SegmentContext";
import { SegmentProvider } from "@/contexts/SegmentProvider";
import { restorePrincipal } from "@/principal";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConfigCatProvider } from "configcat-react";
import React from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider } from "./contexts/ThemeProvider";
import "./index.css";
import { DesktopProvider } from "@/contexts/DesktopProvider";
import { appMainService, runnerMainService } from "./ipc";
import { startVersionTracker } from "@/version-tracker";
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'

// Restoring principal from local storage before warming up the query client here.
restorePrincipal();

// Start tracking version of the management plane
startVersionTracker();

const queryClient = new QueryClient();
queryClient.setDefaultOptions({
    queries: {
        refetchOnReconnect: true,
        staleTime: 1_000 * 5, // 5 seconds
    },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <SegmentProvider create={() => createSegment()}>
            <ConfigCatProvider
                sdkKey={"configcat-proxy/default"}
                options={{
                    baseUrl: window.origin + "/feature-flags/configcat",
                    setupHooks: (hooks) => hooks.on("clientReady", () => console.log("ConfigCat client is ready!")),
                }}
            >
                <ThemeProvider>
                    <DesktopProvider
                        runnerService={runnerMainService}
                        isDesktop={!!runnerMainService}
                        appMainService={appMainService}
                    >
                        <GitpodAPIContext.Provider value={GitpodAPI}>
                            <QueryClientProvider client={queryClient}>
                                <ExceptionErrorBoundary>
                                    <OnboardingQuestionnaireProvider>
                                        <OnboardingProvider>
                                            <NuqsAdapter>
                                                <ReactQueryDevtools initialIsOpen={false} />
                                                <App />
                                                <Toaster />
                                            </NuqsAdapter>
                                        </OnboardingProvider>
                                    </OnboardingQuestionnaireProvider>
                                </ExceptionErrorBoundary>
                            </QueryClientProvider>
                        </GitpodAPIContext.Provider>
                    </DesktopProvider>
                </ThemeProvider>
            </ConfigCatProvider>
        </SegmentProvider>
    </React.StrictMode>,
);
